/**
 * Toast — lightweight, self-contained transient notification.
 *
 * A single `showToast(message, opts)` entry point any module can call to flash a
 * non-blocking message near the top of the screen (e.g. "Couldn't join that
 * room", "Connection lost"). Toasts stack, auto-dismiss, and clear on tap. Pure
 * DOM + injected CSS, no dependencies. z-index 600 — below the chat bubble (700)
 * and coach marks (760), above normal UI. Sits clear of the top header bar.
 */

export type ToastType = 'error' | 'info' | 'success';

export interface ToastOptions {
  /** Accent + intent. Defaults to 'info'. */
  type?: ToastType;
  /** Auto-dismiss delay in ms. Defaults to 4000. */
  durationMs?: number;
}

const STYLE_ID = 'sdl-toast-styles';
const CONTAINER_ID = 'sdl-toast-container';

const ACCENT: Record<ToastType, string> = {
  error: '#FF3366',
  info: '#00E5FF',
  success: '#00FF88',
};

const ICON: Record<ToastType, string> = {
  error: '!',
  info: 'i',
  success: '✓',
};

function ensureContainer(): HTMLElement {
  injectStyles();
  let container = document.getElementById(CONTAINER_ID);
  if (!container) {
    container = document.createElement('div');
    container.id = CONTAINER_ID;
    document.body.appendChild(container);
  }
  return container;
}

/** Flash a transient toast. Safe to call from anywhere, any number of times. */
export function showToast(message: string, opts: ToastOptions = {}): void {
  const type = opts.type ?? 'info';
  const duration = opts.durationMs ?? 4000;
  const container = ensureContainer();

  const toast = document.createElement('div');
  toast.className = `sdl-toast sdl-toast-${type}`;
  toast.style.setProperty('--toast-accent', ACCENT[type]);

  const icon = document.createElement('span');
  icon.className = 'sdl-toast-icon';
  icon.textContent = ICON[type];

  const text = document.createElement('span');
  text.className = 'sdl-toast-text';
  text.textContent = message;

  toast.append(icon, text);
  container.appendChild(toast);

  // Next frame → trigger the slide/fade-in transition.
  requestAnimationFrame(() => toast.classList.add('visible'));

  let dismissed = false;
  const dismiss = (): void => {
    if (dismissed) return;
    dismissed = true;
    window.clearTimeout(timer);
    toast.classList.remove('visible');
    // Remove after the fade-out; a timeout backstops a missed transitionend.
    toast.addEventListener('transitionend', () => toast.remove(), { once: true });
    window.setTimeout(() => toast.remove(), 400);
  };

  const timer = window.setTimeout(dismiss, duration);
  toast.addEventListener('click', dismiss);
}

/**
 * Flash a transient toast anchored directly ABOVE a specific element (a tapped
 * button), with a little arrow pointing at it. Use for validation / action
 * feedback that must be impossible to miss — the top-of-screen showToast and a
 * button-text flash both get overlooked (players read "Add details" on the
 * button as "broken", not as "write more"). Pointer-events:none so it never
 * eats the next tap; auto-dismisses. Re-anchors on scroll/resize while up.
 */
export function showAnchoredToast(anchor: HTMLElement, message: string, opts: ToastOptions = {}): void {
  const type = opts.type ?? 'error';
  const duration = opts.durationMs ?? 3200;
  injectStyles();

  const bubble = document.createElement('div');
  bubble.className = `sdl-anchored-toast sdl-anchored-toast-${type}`;
  bubble.style.setProperty('--toast-accent', ACCENT[type]);
  bubble.textContent = message;
  document.body.appendChild(bubble);

  const position = (): void => {
    const r = anchor.getBoundingClientRect();
    // Centre horizontally on the button, clamped so the bubble can't run off
    // either screen edge (the bubble is translateX(-50%), max-width ~320px).
    const half = Math.min(bubble.offsetWidth, window.innerWidth * 0.82) / 2;
    const cx = Math.max(half + 8, Math.min(window.innerWidth - half - 8, r.left + r.width / 2));
    bubble.style.left = `${cx}px`;
    // Sit just above the button's top edge.
    bubble.style.bottom = `${Math.max(8, window.innerHeight - r.top + 10)}px`;
  };
  position();

  // rAF: commit the hidden start state (opacity 0 / shifted down) before adding
  // .visible so the slide-up is a real transition, not a first-frame flash on
  // Android WebView. See the entrance-animation rule in CLAUDE.md.
  requestAnimationFrame(() => bubble.classList.add('visible'));

  const reflow = (): void => position();
  window.addEventListener('scroll', reflow, true);
  window.addEventListener('resize', reflow);

  let dismissed = false;
  const dismiss = (): void => {
    if (dismissed) return;
    dismissed = true;
    window.clearTimeout(timer);
    window.removeEventListener('scroll', reflow, true);
    window.removeEventListener('resize', reflow);
    bubble.classList.remove('visible');
    bubble.addEventListener('transitionend', () => bubble.remove(), { once: true });
    window.setTimeout(() => bubble.remove(), 300);
  };
  const timer = window.setTimeout(dismiss, duration);
}

function injectStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    #${CONTAINER_ID} {
      position: fixed;
      /* Sit clear of the top header/back-button row. */
      top: calc(env(safe-area-inset-top, 0px) + 62px);
      left: 50%;
      transform: translateX(-50%);
      z-index: 600;
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 10px;
      pointer-events: none;
      width: max-content;
      max-width: min(92vw, 440px);
    }

    .sdl-toast {
      pointer-events: auto;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 11px;
      max-width: 100%;
      padding: 13px 18px;
      border-radius: 14px;
      /* Near-opaque so the busy lobby/scene behind never bleeds through the
         text; blur adds extra separation where supported. */
      background: rgba(18, 18, 24, 0.98);
      -webkit-backdrop-filter: blur(14px);
      backdrop-filter: blur(14px);
      border: 1.5px solid var(--toast-accent, #00E5FF);
      box-shadow: 0 12px 32px rgba(0, 0, 0, 0.6);
      color: #fff;
      font-family: 'Rajdhani', sans-serif;
      font-size: 15.5px;
      font-weight: 600;
      line-height: 1.3;
      letter-spacing: 0.3px;
      opacity: 0;
      transform: translateY(-12px) scale(0.96);
      transition: opacity 0.25s ease, transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
    }

    .sdl-toast.visible {
      opacity: 1;
      transform: translateY(0) scale(1);
    }

    .sdl-toast-icon {
      flex: 0 0 auto;
      width: 22px;
      height: 22px;
      border-radius: 50%;
      background: var(--toast-accent, #00E5FF);
      color: #0a0a0e;
      font-size: 14px;
      font-weight: 800;
      font-style: normal;
      line-height: 22px;
      text-align: center;
    }

    .sdl-toast-text {
      flex: 1 1 auto;
      text-align: left;
    }

    /* Anchored-above-a-button variant. z-index above modals/sheets so the
       feedback is always seen, even over the Settings panel or a dialog. */
    .sdl-anchored-toast {
      position: fixed;
      z-index: 3000;
      max-width: min(82vw, 320px);
      padding: 10px 14px;
      border-radius: 12px;
      background: rgba(18, 18, 24, 0.98);
      -webkit-backdrop-filter: blur(14px);
      backdrop-filter: blur(14px);
      border: 1.5px solid var(--toast-accent, #FF3366);
      box-shadow: 0 10px 26px rgba(0, 0, 0, 0.6);
      color: #fff;
      font-family: 'Rajdhani', sans-serif;
      font-size: 14.5px;
      font-weight: 600;
      line-height: 1.3;
      letter-spacing: 0.3px;
      text-align: center;
      pointer-events: none;
      opacity: 0;
      transform: translate(-50%, 8px);
      transition: opacity 0.2s ease, transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1);
    }
    .sdl-anchored-toast.visible {
      opacity: 1;
      transform: translate(-50%, 0);
    }
    /* Little arrow pointing down at the button it's anchored to. */
    .sdl-anchored-toast::after {
      content: '';
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      border: 6px solid transparent;
      border-top-color: var(--toast-accent, #FF3366);
    }
  `;
  document.head.appendChild(style);
}
